import { 
    AlertDialog, 
    AlertDialogBody, 
    AlertDialogContent, 
    AlertDialogFooter, 
    AlertDialogHeader, 
    AlertDialogOverlay, 
    Button, 
    Divider, 
    Flex, 
    Text
} from "@chakra-ui/react";
import { useCreditCard } from "@/hooks/useCreditCard";
import { useRef } from "react";
import { useParams } from "react-router-dom";
import { showToast } from "@/utils";
import { useAsyncFunction } from "@/hooks/useAsyncFunction";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Input } from "@/components";
import { EditIcon2 } from "@/styles/icons";

interface RenameCreditCardProps {
    isOpen: boolean;
    onClose: ()=> void;
}
const renameSchema = z.object({
    cardName: z.string().optional(),
})
type RenameFormValues = z.infer<typeof renameSchema>
export default function RenameCreditCard({isOpen, onClose}: RenameCreditCardProps) {
    const { 
        renameCreditCard,
        setSelectedCreditCardName
    } = useCreditCard();
    const modalRef = useRef();
    const { id } = useParams();
    const { execute, loading } = useAsyncFunction(renameCreditCard);
    const renameForm = useForm<any>({
        resolver: zodResolver(renameSchema),
        shouldFocusError: false,
    })
    
    async function onSubmit(data: RenameFormValues) {
        try {
            const formData = {
                idCard: id,
                cardName: data.cardName
            }
            await execute(formData);
            setSelectedCreditCardName(data.cardName);
            showToast({
                type: "success",
                message: `Cartão renomeado com sucesso.`,
            });      
        } catch (error) {
            error?.response?.data?.errors.map((error) => {
            showToast({
                type: "error",
                message: `${error?.code} - ${error?.description}`,
            });
        });
        } finally {
            onClose();
        }
    }
    return (
        <AlertDialog
            isOpen={isOpen}
            onClose={onClose}
            leastDestructiveRef={modalRef}
            motionPreset="slideInBottom"
            isCentered
        >
        <AlertDialogOverlay>
            <AlertDialogContent
                display="flex"
                flexDir="column"
                borderRadius=".5rem"
                w="400px"

            >
                <AlertDialogHeader
                    p="16px 24px" 
                    fontSize="1rem" 
                    fontWeight="bold"
                >
                    <Flex
                        align="center"
                        gap="4"
                    >
                        <EditIcon2 size={24}/>
                        <Text
                            fontSize="1rem"
                            fontWeight={600}
                        >
                            Renomear cartão
                        </Text>
                    </Flex>
                </AlertDialogHeader>
                <Divider />
                <AlertDialogBody
                    margin="0"
                    px="1rem"
                    py="1rem"
                    w="100%"
                >
                    <Flex
                        w="100%"
                        flexDirection="column"
                        gap="1rem"
                        as="form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            renameForm.handleSubmit(onSubmit)(e);
                        }}
                    >
                        <Input
                            label="Nome do cartão"
                            placeholder="Renomear cartão"
                            name="cardName"
                            onChange={(e) => renameForm.setValue("cardName", e.target.value)}
                            register={{ ...renameForm.register("cardName") }}
                            errors={renameForm.formState.errors.cardName?.message}
                            _focus={{
                                borderColor: "black",
                            }}
                            maxLength={20}
                        />
                    </Flex>
                </AlertDialogBody>
                <Divider />
                <AlertDialogFooter
                    h="5rem"
                    w="100%"
                    display="flex"
                    gap="4"
                >
                    <Button
                        variant="secondary"
                        size="sm"
                        borderRadius=".5rem"
                        fontWeight={400}
                        transition="filter .2s"
                        _hover={{
                            transition: "filter .2s",
                            filter: "brightness(.9)",
                        }}
                        _focus={{}}
                        onClick={onClose}
                    >
                        Voltar
                    </Button>
                    <Button
                        variant="primary2"
                        size="sm"
                        borderRadius=".5rem"
                        fontWeight={400}
                        transition="filter .2s"
                        _hover={{
                            transition: "filter .2s",
                            filter: "brightness(.9)",
                        }}
                        _focus={{}}
                        type="submit"
                        isLoading={loading}
                        isDisabled={loading || renameForm.watch("cardName") === ""}
                        onClick={() => {
                            renameForm.handleSubmit(onSubmit)();
                        }}
                    >
                        Confirmar
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialogOverlay>
        </AlertDialog>
    );
};
